import { ref, onMounted, onBeforeUnmount } from "vue";
import getScrollTarget from "@/v2/shared/getScrollTarget.js";
import useWindowDimensions from "@/util/useWindowDimensions.ts";

export default function useScrollDrag(options) {
  const {
    container = null,
    enabled = ref(true),
    direction = "vertical",
    onScroll = () => {},
  } = options;

  const { isMobileViewport } = useWindowDimensions();

  const timeout = ref(null);
  const dragging = ref(false);

  async function onMouseMove(e) {
    if (!dragging.value || !enabled.value || isMobileViewport.value) {
      return;
    }

    const scroller = getScrollTarget(container);

    const attributes =
      direction === "vertical"
        ? ["scrollTop", "movementY"]
        : ["scrollLeft", "movementX"];

    scroller[attributes[0]] += -e[attributes[1]];

    onScroll(e);
  }

  function onMouseUp() {
    if (!enabled.value || isMobileViewport.value) {
      dragging.value = false;
      return;
    }

    clearTimeout(timeout.value);

    setTimeout(() => {
      dragging.value = false;
    });
  }

  function onMouseDown() {
    if (!enabled.value || isMobileViewport.value) {
      return;
    }

    timeout.value = setTimeout(() => {
      dragging.value = true;
    }, 130);
  }

  function onClick(e) {
    if (dragging.value) {
      e.stopPropagation();
    }
  }

  const eventMap = {
    mousemove: onMouseMove,
    mousedown: onMouseDown,
    mouseup: onMouseUp,
    mouseleave: onMouseUp,
    click: onClick,
  };

  function setupListeners() {
    const scrollTarget = getScrollTarget(container);

    Object.keys(eventMap).forEach((eventToHandle) => {
      scrollTarget?.addEventListener(eventToHandle, eventMap[eventToHandle], {
        capture: eventToHandle === "click",
      });
    });
  }

  function removeListeners() {
    const scrollTarget = getScrollTarget(container);

    Object.keys(eventMap).forEach((eventToHandle) => {
      scrollTarget?.removeEventListener(eventToHandle, eventMap[eventToHandle]);
    });
  }

  onMounted(setupListeners);
  onBeforeUnmount(removeListeners);

  return {
    setupListeners,
    removeListeners,
  };
}
